import React from 'react';
import { Box, Container, Grid, Typography, Divider, Link, IconButton, SvgIcon } from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InfoIcon from '@mui/icons-material/Info';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { isProfessionalAcquisitionAllowed } from '../../utils/nativePlatform';

// Styled components
const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
  padding: '2rem 0',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  marginBottom: '1rem',
  fontSize: '1.125rem',
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  fontSize: '0.875rem',
  marginBottom: '0.25rem',
  display: 'block',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: theme.palette.primary.main,
    transform: 'translateY(-3px)',
    backgroundColor: theme.palette.primary.main + '10',
  },
}));

const TikTokIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02c.08 1.53.63 3.09 1.75 4.17c1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97c-.57-.26-1.1-.59-1.62-.93c-.01 2.92.01 5.84-.02 8.75c-.03 1.4-.54 2.79-1.35 3.94c-1.31 1.92-3.58 3.17-5.91 3.21c-1.43.08-2.86-.31-4.08-1.03c-1.81-1.13-2.93-3.12-2.98-5.24c.12-1.66.88-3.22 2.19-4.29c1.22-1.06 2.87-1.66 4.49-1.55v4.21c-.74-.06-1.47.11-2.1.49c-.6.37-.99.98-1.08 1.68c-.14.77.14 1.58.7 2.1c.54.51 1.3.73 2.05.6c.76-.1 1.48-.59 1.83-1.27c.22-.44.31-.92.3-1.42c-.04-6.39-.01-12.78-.01-19.17z" />
  </SvgIcon>
);

const Footer = () => {
  const { t } = useTranslation();
  const brandName = t('brand.name');
  const professionalAcquisitionAllowed = isProfessionalAcquisitionAllowed();

  return (
    <FooterContainer>
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="center">
          {/* Column 1: Pour les clients */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterTitle variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ mr: 1, fontSize: '1.25rem' }} /> {t('footer.for_clients')}
            </FooterTitle>
            <FooterLink component={RouterLink} to="/search-salon">{t('footer.find_salon')}</FooterLink>
            <FooterLink component={RouterLink} to="/search-salon">{t('footer.book_appointment')}</FooterLink>
            <FooterLink component={RouterLink} to="/search-salon">{t('footer.explore_services')}</FooterLink>
            <FooterLink component={RouterLink} to="/search-salon">{t('footer.special_offers')}</FooterLink>
          </Grid>

          {/* Column 2: Pour les professionnels */}
          {professionalAcquisitionAllowed && (
            <Grid item xs={12} sm={6} md={3}>
              <FooterTitle variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <StorefrontIcon sx={{ mr: 1, fontSize: '1.25rem' }} /> {t('footer.for_professionals')}
              </FooterTitle>
              <FooterLink component={RouterLink} to="/?bypass=true&type=pro#proSection">{t('footer.register_salon')}</FooterLink>
              <FooterLink component={RouterLink} to="/?bypass=true&type=pro#proSection">{t('footer.manage_appointments')}</FooterLink>
              <FooterLink component={RouterLink} to="/?bypass=true&type=pro#proSection">{t('footer.dashboard')}</FooterLink>
              <FooterLink component={RouterLink} to="/?bypass=true&type=pro#proSection">{t('footer.pricing')}</FooterLink>
            </Grid>
          )}

          {/* Column 3: À propos de APP_NAME */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterTitle variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1, fontSize: '1.25rem' }} /> {t('footer.about', { appName: brandName })}
            </FooterTitle>
            <FooterLink component={RouterLink} to="/a-propos">{t('footer.our_mission')}</FooterLink>
            <FooterLink component={RouterLink} to="/equipe">{t('footer.our_team')}</FooterLink>
            <FooterLink component={RouterLink} to="/contact">{t('footer.contact_us')}</FooterLink>
            <FooterLink component={RouterLink} to="/carriere">{t('footer.join_us')}</FooterLink>
          </Grid>

          {/* Column 4: Social Media */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <FooterTitle variant="h6">{brandName}</FooterTitle>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {t('footer.follow_us')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <SocialIconButton
                  component="a"
                  href="https://www.instagram.com/saloone365?igsh=MXRrbjJrbWE1bWdlbA=="
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <InstagramIcon />
                </SocialIconButton>
                <SocialIconButton
                  component="a"
                  href="https://www.facebook.com/profile.php?id=61585698232668"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FacebookIcon />
                </SocialIconButton>
                <SocialIconButton
                  component="a"
                  href="https://www.tiktok.com/@saloone_app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                >
                  <TikTokIcon />
                </SocialIconButton>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Bottom section with contact and copyright */}
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 1, fontSize: '1rem' }} />
                <Typography variant="body2">{t('footer.find_near_you')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, fontSize: '1rem' }} />
                <Typography variant="body2">contact@saloone.fr</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 2, flexWrap: 'wrap' }}>
              <FooterLink href="/legal/presentation">{t('footer.legal_presentation')}</FooterLink>
              <FooterLink href="/legal/confidentialite">{t('footer.legal_privacy')}</FooterLink>
              <FooterLink href="/legal/conditions">{t('footer.legal_terms')}</FooterLink>
              <FooterLink href="/delete-account">{t('footer.delete_account')}</FooterLink>
              <FooterLink href="/login">{t('footer.site_management')}</FooterLink>
            </Box>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.tertiary">
            {t('footer.copyright', {
              year: new Date().getFullYear(),
              appName: brandName,
              version: `${process.env.REACT_APP_GIT_COMMIT_HASH}.${process.env.REACT_APP_DEPLOY_ENV}`,
            })}
          </Typography>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
